import React, { useCallback, useMemo, useState } from 'react';

import useSubmitFormStore from '@/modules/HealthPlan/HealthPlanSetup/stores/useSubmitForm.store';
import { usePlansByPlanYearQuery } from '@/modules/HealthPlan/queries';

import { useHealthPlanNameStore } from '../../BasicProperties/HealthPlanName/stores';

import { useHealthPlanCoverageLevels } from './index';

/**
 * Container hook for CoverageLevels component.
 * Encapsulates modal state, plan filtering, and validation state.
 */
const useCoverageLevelsContainer = () => {
  const [selectModalVisible, setSelectModalVisible] = useState(false);
  const activateValidate = useSubmitFormStore((state) => state.activateValidate);

  const handleToggleModal = useCallback(() => {
    setSelectModalVisible((prev) => !prev);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectModalVisible(false);
  }, []);

  const handleShowAddButton = useCallback((e: React.SyntheticEvent<Element, Event>) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectModalVisible(true);
  }, []);

  const { levels, excludeIds } = useHealthPlanCoverageLevels();

  const currentHealthPlanNameState = useHealthPlanNameStore((state) => state.state);

  const { data: planYearPlans, isLoading: isPlansLoading } = usePlansByPlanYearQuery({
    id: currentHealthPlanNameState?.planYearId,
  });

  const linkedPlanIds = useMemo(() => {
    const plans = currentHealthPlanNameState.plans;
    if (plans && plans.length > 0) {
      return plans.filter((item): item is string => typeof item === 'string');
    }
    return [];
  }, [currentHealthPlanNameState.plans]);

  const filteredPlans = useMemo(
    () => planYearPlans.filter((plan) => linkedPlanIds.includes(plan.id)),
    [linkedPlanIds, planYearPlans]
  );

  return {
    selectModalVisible,
    activateValidate,
    levels,
    excludeIds,
    filteredPlans,
    isPlansLoading,
    handleToggleModal,
    handleCloseModal,
    handleShowAddButton,
  };
};

export default useCoverageLevelsContainer;
